<script module lang="ts">
  import { Howl } from 'howler';
  import { options } from '../../store';
  import { SoundType as soundTypeImport } from '../models/sound';
  // Re-exported so consumers can do `import { playAudio, SoundType } from './audio.svelte'`.
  // NOTE: this `let` shadows the name in TYPE position too, so type annotations below must
  // use `soundTypeImport` -- writing `: SoundType` refers to this variable, not the enum.
  export let SoundType = soundTypeImport;

  let sound: Howl;
  // Partial: the enum carries more members than there are recorded clips for.
  const soundDefinitions: Partial<Record<soundTypeImport, string[]>> = {
    [SoundType.AttackSlash]: ['slash1', 'slash2', 'slash3'],
    [SoundType.AttackPierce]: ['stab1', 'stab2', 'stab3'],
    [SoundType.AttackBlunt]: ['blunt1', 'blunt2', 'blunt3'],
    [SoundType.AttackRend]: ['rend1', 'rend2'],

    [SoundType.MagicFire]: ['fire1', 'fire2', 'fire3'],
    [SoundType.MagicIce]: ['ice1', 'ice2'],
    [SoundType.MagicLightning]: ['lightning1', 'lightning2', 'lightning3'],
    [SoundType.MagicEarth]: ['earth1', 'earth2', 'earth3'],

    [SoundType.PainBird]: ['birdPain1', 'birdPain2', 'birdPain3', 'birdPain4', 'birdPain5'],
    [SoundType.PainHumanMale]: [
      'humanMalePain1',
      'humanMalePain2',
      'humanMalePain3',
      'humanMalePain4',
    ],
    [SoundType.PainInsect]: ['insectPain1', 'insectPain2', 'insectPain3'],
    [SoundType.PainOrcMale]: ['orcMalePain1', 'orcMalePain2', 'orcMalePain3'],

    // [SoundType.DefenseBlock]: ['block1', 'block2', 'block3'],
    [SoundType.Dodge]: ['dodge1', 'dodge2', 'dodge3'],

    [SoundType.DiceSingle]: ['singleDie1', 'singleDie2', 'singleDie3', 'singleDie4'],
    [SoundType.DiceMultiple]: ['multipleDice1', 'multipleDice2'],

    [SoundType.Button]: ['button1'],
    [SoundType.BattleStart]: ['battleStart'],
    [SoundType.Swish]: ['dodge1'], // Using dodge3 as a swish sound for now
    [SoundType.ManualOpen]: ['manualOpen'],
    [SoundType.ManualClose]: ['manualClose'],
  };

  export function playAudio(audio: soundTypeImport) {
    // Audio playback is currently disabled -- everything below is intentionally dead.
    return;
    // sound.rate(4);
    // `?? []` rather than a guard: the early return above makes this unreachable, and
    // TypeScript does not narrow types in dead code, so a guard would not type-check.
    const sounds = soundDefinitions[audio] ?? [];
    if (!sounds.length) return;

    const index = Math.floor(randomFloat(0, sounds.length));
    const soundName = sounds[index];

    sound.play(soundName);
  }
</script>

<script lang="ts">
  import { randomFloat } from '../utils/math';
  import { onMount, onDestroy } from 'svelte';

  // attack sounds
  import slash1 from '../../assets/sounds/slash-1.mp3';
  import slash2 from '../../assets/sounds/slash-2.mp3';
  import slash3 from '../../assets/sounds/slash-3.mp3';
  import stab1 from '../../assets/sounds/stab-1.mp3';
  import stab2 from '../../assets/sounds/stab-2.mp3';
  import stab3 from '../../assets/sounds/stab-3.mp3';
  import blunt1 from '../../assets/sounds/blunt-1.mp3';
  import blunt2 from '../../assets/sounds/blunt-2.mp3';
  import blunt3 from '../../assets/sounds/blunt-3.mp3';
  import rend1 from '../../assets/sounds/rend-1.mp3';
  import rend2 from '../../assets/sounds/rend-2.mp3';

  // magic sounds
  import fire1 from '../../assets/sounds/fire-1.mp3';
  import fire2 from '../../assets/sounds/fire-2.mp3';
  import fire3 from '../../assets/sounds/fire-3.mp3';
  import ice1 from '../../assets/sounds/ice-1.mp3';
  import ice2 from '../../assets/sounds/ice-2.mp3';
  import lightning1 from '../../assets/sounds/lightning-1.mp3';
  import lightning2 from '../../assets/sounds/lightning-2.mp3';
  import lightning3 from '../../assets/sounds/lightning-3.mp3';
  import earth1 from '../../assets/sounds/earth-1.mp3';
  import earth2 from '../../assets/sounds/earth-2.mp3';
  import earth3 from '../../assets/sounds/earth-3.mp3';
  /* import poison1 from '../../assets/sounds/poison-1.mp3';
  import poison2 from '../../assets/sounds/poison-2.mp3';
  import poison3 from '../../assets/sounds/poison-3.mp3'; */
  /* import heal1 from '../../assets/sounds/heal-1.mp3';
  import heal2 from '../../assets/sounds/heal-2.mp3';
  import heal3 from '../../assets/sounds/heal-3.mp3'; */

  // pain sounds
  import birdPain1 from '../../assets/sounds/bird-pain-1.mp3';
  import birdPain2 from '../../assets/sounds/bird-pain-2.mp3';
  import birdPain3 from '../../assets/sounds/bird-pain-3.mp3';
  import birdPain4 from '../../assets/sounds/bird-pain-4.mp3';
  import birdPain5 from '../../assets/sounds/bird-pain-5.mp3';
  import humanMalePain1 from '../../assets/sounds/human-male-pain-1.mp3';
  import humanMalePain2 from '../../assets/sounds/human-male-pain-2.mp3';
  import humanMalePain3 from '../../assets/sounds/human-male-pain-3.mp3';
  import humanMalePain4 from '../../assets/sounds/human-male-pain-4.mp3';
  import insectPain1 from '../../assets/sounds/insect-pain-1.mp3';
  import insectPain2 from '../../assets/sounds/insect-pain-2.mp3';
  import insectPain3 from '../../assets/sounds/insect-pain-3.mp3';
  import orcMalePain1 from '../../assets/sounds/orc-male-pain-1.mp3';
  import orcMalePain2 from '../../assets/sounds/orc-male-pain-2.mp3';
  import orcMalePain3 from '../../assets/sounds/orc-male-pain-3.mp3';

  // defense sounds
  // import block1 from '../../assets/sounds/block-1.mp3';
  // import block2 from '../../assets/sounds/block-2.mp3';
  // import block3 from '../../assets/sounds/block-3.mp3';
  import dodge1 from '../../assets/sounds/dodge-1.mp3';
  import dodge2 from '../../assets/sounds/dodge-2.mp3';
  import dodge3 from '../../assets/sounds/dodge-3.mp3';

  // dice sounds
  import singleDie1 from '../../assets/sounds/single-die-1.mp3';
  import singleDie2 from '../../assets/sounds/single-die-2.mp3';
  import singleDie3 from '../../assets/sounds/single-die-3.mp3';
  import singleDie4 from '../../assets/sounds/single-die-4.mp3';
  import multipleDice1 from '../../assets/sounds/multiple-dice-1.mp3';
  import multipleDice2 from '../../assets/sounds/multiple-dice-2.mp3';

  //UI sounds
  import button1 from '../../assets/sounds/button-1.mp3';
  import battleStart from '../../assets/sounds/battle-start.mp3';
  import manualOpen from '../../assets/sounds/manual-open.mp3';
  import manualClose from '../../assets/sounds/manual-close.mp3';

  let mergedFiles = slash1.output;

  /* export function queueAudio(audio: string){
    console.log('queueAudio', sound)
    sound.queue(audio);
  } */

  onMount(() => {
    sound = new Howl({
      src: mergedFiles,
      sprite: {
        slash1: [slash1.startTime, slash1.duration],
        slash2: [slash2.startTime, slash2.duration],
        slash3: [slash3.startTime, slash3.duration],
        stab1: [stab1.startTime, stab1.duration],
        stab2: [stab2.startTime, stab2.duration],
        stab3: [stab3.startTime, stab3.duration],
        blunt1: [blunt1.startTime, blunt1.duration],
        blunt2: [blunt2.startTime, blunt2.duration],
        blunt3: [blunt3.startTime, blunt3.duration],
        rend1: [rend1.startTime, rend1.duration],
        rend2: [rend2.startTime, rend2.duration],

        fire1: [fire1.startTime, fire1.duration],
        fire2: [fire2.startTime, fire2.duration],
        fire3: [fire3.startTime, fire3.duration],
        ice1: [ice1.startTime, ice1.duration],
        ice2: [ice2.startTime, ice2.duration],
        lightning1: [lightning1.startTime, lightning1.duration],
        lightning2: [lightning2.startTime, lightning2.duration],
        lightning3: [lightning3.startTime, lightning3.duration],
        earth1: [earth1.startTime, earth1.duration],
        earth2: [earth2.startTime, earth2.duration],
        earth3: [earth3.startTime, earth3.duration],

        birdPain1: [birdPain1.startTime, birdPain1.duration],
        birdPain2: [birdPain2.startTime, birdPain2.duration],
        birdPain3: [birdPain3.startTime, birdPain3.duration],
        birdPain4: [birdPain4.startTime, birdPain4.duration],
        birdPain5: [birdPain5.startTime, birdPain5.duration],
        humanMalePain1: [humanMalePain1.startTime, humanMalePain1.duration],
        humanMalePain2: [humanMalePain2.startTime, humanMalePain2.duration],
        humanMalePain3: [humanMalePain3.startTime, humanMalePain3.duration],
        humanMalePain4: [humanMalePain4.startTime, humanMalePain4.duration],
        insectPain1: [insectPain1.startTime, insectPain1.duration],
        insectPain2: [insectPain2.startTime, insectPain2.duration],
        insectPain3: [insectPain3.startTime, insectPain3.duration],
        orcMalePain1: [orcMalePain1.startTime, orcMalePain1.duration],
        orcMalePain2: [orcMalePain2.startTime, orcMalePain2.duration],
        orcMalePain3: [orcMalePain3.startTime, orcMalePain3.duration],

        // block1: [block1.startTime, block1.duration],
        // block2: [block2.startTime, block2.duration],
        // block3: [block3.startTime, block3.duration],
        dodge1: [dodge1.startTime, dodge1.duration],
        dodge2: [dodge2.startTime, dodge2.duration],
        dodge3: [dodge3.startTime, dodge3.duration],

        singleDie1: [singleDie1.startTime, singleDie1.duration],
        singleDie2: [singleDie2.startTime, singleDie2.duration],
        singleDie3: [singleDie3.startTime, singleDie3.duration],
        singleDie4: [singleDie4.startTime, singleDie4.duration],
        multipleDice1: [multipleDice1.startTime, multipleDice1.duration],
        multipleDice2: [multipleDice2.startTime, multipleDice2.duration],

        button1: [button1.startTime, button1.duration],
        battleStart: [battleStart.startTime, battleStart.duration],
        manualOpen: [manualOpen.startTime, manualOpen.duration],
        manualClose: [manualClose.startTime, manualClose.duration],
      },
    });

    // console.log('onMount', sound);
  });

  onDestroy(() => {
    sound.unload();
  });
</script>

<audio>
  {#each mergedFiles as file}
    <source src={file} type={file.indexOf('.mp3') !== -1 ? 'audio/mpeg' : 'audio/webm'} />
  {/each}
  <p>Your browser does not support the audio element.</p>
</audio>

<style lang="less">
</style>
